/**
 * 사용자관리 — CM-DESIGN-001
 *
 * - 계정은 기관별로 격리한다.
 * - 한 사용자의 복수 부서 소속을 별도 매핑으로 관리한다.
 * - 로그인 ID는 최초 저장 후 변경할 수 없다.
 * - API: cm_user_account + hr_sys_user(로그인) + cm_user_org_map
 */
import { isBdApiEnabled } from 'config/bdApiConfig';
import { commonSelect, cacheGet, cacheSet } from 'services/commonQuery';
import api from 'services/api';


const USER_STORAGE_PREFIX = 'erp.master.userAccount';
const ORG_MAP_STORAGE_PREFIX = 'erp.master.userOrgMap';

export const ACCOUNT_TYPE_OPTIONS = [
  { value: 'USER', label: '일반사용자' },
  { value: 'TENANT_ADMIN', label: '기관관리자' },
];

export const ACCOUNT_STATUS_OPTIONS = [
  { value: 'ACTIVE', label: '정상' },
  { value: 'LOCKED', label: '잠금' },
  { value: 'DISABLED', label: '사용중지' },
];

export const MEMBERSHIP_STATUS_OPTIONS = [
  { value: 'ACTIVE', label: '소속' },
  { value: 'CLOSED', label: '해제' },
];

function storageKey(prefix, tenantCode) {
  return `${prefix}|${String(tenantCode || '').trim()}`;
}

export function createAccountUid(prefix = 'UA') {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function readRows(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const data = JSON.parse(raw);
    return Array.isArray(data?.rows) ? data.rows : [];
  } catch {
    return [];
  }
}

function writeRows(key, rows) {
  const saved = (rows || []).map((r) => ({ ...r, mod: '' }));
  localStorage.setItem(
    key,
    JSON.stringify({ rows: saved, savedAt: new Date().toISOString() })
  );
  return saved;
}

export function accountTypeLabel(value) {
  return ACCOUNT_TYPE_OPTIONS.find((o) => o.value === value)?.label || value || '';
}

export function accountStatusLabel(value) {
  return ACCOUNT_STATUS_OPTIONS.find((o) => o.value === value)?.label || value || '';
}

function isDbOrgUnitId(id) {
  return id != null && id !== '' && /^\d+$/.test(String(id));
}

const userCache = new Map();
const orgMapCache = new Map();

function mapApiUser(row) {
  const orgUnitId = isDbOrgUnitId(row.orgUnitId) ? String(row.orgUnitId) : '';
  return {
    ...row,
    accountUid: row.accountUid || createAccountUid(),
    loginId: row.loginId || '',
    userNm: row.userNm || '',
    deptUid: orgUnitId,
    orgUnitId: orgUnitId || undefined,
    deptCode: row.deptCode || '',
    deptName: row.deptName || '',
    email: row.email || '',
    phone: row.phone || '',
    accountType: row.accountType || 'USER',
    status: row.status || 'ACTIVE',
    failedLoginCnt: Number(row.failedLoginCnt) || 0,
    lastLoginAt: row.lastLoginAt || '',
    password: '',
    sortOrder: row.sortOrder ?? 10,
    remark: row.remark || '',
    mod: '',
  };
}

function mapApiOrg(row) {
  const orgUnitId = isDbOrgUnitId(row.orgUnitId) ? String(row.orgUnitId) : '';
  return {
    ...row,
    mapUid: row.mapUid || createAccountUid('UM'),
    accountUid: row.accountUid || '',
    deptUid: orgUnitId,
    orgUnitId: orgUnitId || undefined,
    deptCode: row.deptCode || '',
    deptName: row.deptName || '',
    status: row.status || 'ACTIVE',
    remark: row.remark || '',
    mod: '',
  };
}

function toUserSaveRow(row) {
  const deptUid = String(row.deptUid || '').trim();
  return {
    mod: row.mod,
    accountUid: row.accountUid,
    loginId: String(row.loginId || '').trim(),
    userNm: String(row.userNm || '').trim(),
    email: row.email || '',
    phone: row.phone || '',
    accountType: row.accountType,
    status: row.status,
    password: String(row.password || '').trim(),
    deptUid,
    deptCode: String(row.deptCode || '').trim(),
    orgUnitId: isDbOrgUnitId(deptUid) ? Number(deptUid) : null,
  };
}

function toOrgSaveRow(row) {
  const deptUid = String(row.deptUid || '').trim();
  return {
    mod: row.mod,
    mapUid: row.mapUid,
    accountUid: row.accountUid,
    deptUid,
    deptCode: String(row.deptCode || '').trim(),
    orgUnitId: isDbOrgUnitId(deptUid) ? Number(deptUid) : null,
    status: row.status || 'ACTIVE',
  };
}

/** 현재 로그인 관리자를 최초 사용자로 등록한다. (localStorage PoC) */
export function loadUsersLocal(tenantCode, currentUser = {}) {
  const key = storageKey(USER_STORAGE_PREFIX, tenantCode);
  const existing = readRows(key);
  if (existing.length) return existing.map((r) => ({ ...r, mod: '' }));

  const loginId = String(currentUser?.id || currentUser?.username || 'admin').trim() || 'admin';
  const seeded = [
    {
      accountUid: createAccountUid(),
      loginId,
      userNm: currentUser?.name || '관리자',
      deptUid: '',
      deptCode: '',
      deptName: '',
      email: '',
      phone: '',
      accountType: 'TENANT_ADMIN',
      status: 'ACTIVE',
      failedLoginCnt: 0,
      lastLoginAt: '',
      remark: '현재 로그인 기관 관리자',
      sortOrder: 10,
      mod: '',
    },
  ];
  return writeRows(key, seeded);
}

export async function loadUsers(tenantCode, currentUser = {}) {
  const tc = String(tenantCode || '').trim();
  if (isBdApiEnabled()) {
    const rows = (await commonSelect('CmUserAccount.selectUserAccountList', { tenantCode: tc })).map(
      mapApiUser
    );
    cacheSet(userCache, tc, rows);
    return rows;
  }
  const rows = loadUsersLocal(tenantCode, currentUser);
  cacheSet(userCache, tc, rows);
  return rows;
}

/** 동기 lookup — loadUsers 이후 캐시 */
export function getCachedUsers(tenantCode) {
  const tc = String(tenantCode || '').trim();
  const cached = cacheGet(userCache, tc);
  if (cached) return cached;
  if (!isBdApiEnabled()) {
    return readRows(storageKey(USER_STORAGE_PREFIX, tenantCode)).map((r) => ({ ...r, mod: '' }));
  }
  return [];
}

export function saveUsers(tenantCode, rows) {
  const saved = writeRows(
    storageKey(USER_STORAGE_PREFIX, tenantCode),
    (rows || [])
      .filter((r) => r && r.mod !== 'D')
      .map((r) => ({
        ...r,
        accountUid: r.accountUid || createAccountUid(),
        loginId: String(r.loginId || '').trim(),
        userNm: String(r.userNm || '').trim(),
        password: '',
      }))
  );
  cacheSet(userCache, String(tenantCode || '').trim(), saved);
  return saved;
}

export async function loadUserOrgMaps(tenantCode) {
  const tc = String(tenantCode || '').trim();
  if (isBdApiEnabled()) {
    const rows = (await commonSelect('CmUserOrgMap.selectUserOrgMapList', { tenantCode: tc })).map(
      mapApiOrg
    );
    cacheSet(orgMapCache, tc, rows);
    return rows;
  }
  const rows = readRows(storageKey(ORG_MAP_STORAGE_PREFIX, tenantCode)).map((r) => ({
    ...r,
    mod: '',
  }));
  cacheSet(orgMapCache, tc, rows);
  return rows;
}

export function getCachedUserOrgMaps(tenantCode) {
  const tc = String(tenantCode || '').trim();
  return cacheGet(orgMapCache, tc) || [];
}

export function saveUserOrgMaps(tenantCode, rows) {
  const saved = writeRows(
    storageKey(ORG_MAP_STORAGE_PREFIX, tenantCode),
    (rows || [])
      .filter((r) => r && r.mod !== 'D')
      .map((r) => ({
        ...r,
        mapUid: r.mapUid || createAccountUid('UM'),
      }))
  );
  cacheSet(orgMapCache, String(tenantCode || '').trim(), saved);
  return saved;
}

export async function persistUsers(tenantCode, users, orgMaps) {
  const tc = String(tenantCode || '').trim();
  if (!isBdApiEnabled()) {
    saveUsers(tenantCode, users);
    saveUserOrgMaps(tenantCode, orgMaps);
    return { users: getCachedUsers(tc), orgMaps: getCachedUserOrgMaps(tc) };
  }
  const dirtyUsers = (users || []).filter((r) => r && (r.mod === 'A' || r.mod === 'U' || r.mod === 'D'));
  const dirtyMaps = (orgMaps || []).filter((r) => r && (r.mod === 'A' || r.mod === 'U' || r.mod === 'D'));
  if (dirtyUsers.length || dirtyMaps.length) {
    const res = await api.post('/system/user/save', {
      users: dirtyUsers.map(toUserSaveRow),
      orgMaps: dirtyMaps.map(toOrgSaveRow),
    });
    const data = res?.data || {};
    if (data.success === false || (data.Msg && String(data.Msg).toLowerCase() !== 'success')) {
      throw new Error(data.Msg || '사용자 저장에 실패했습니다.');
    }
  }
  const nextUsers = await loadUsers(tc);
  const nextMaps = await loadUserOrgMaps(tc);
  return { users: nextUsers, orgMaps: nextMaps };
}

export function filterUsers(rows, { loginId, userNm, status } = {}) {
  const id = String(loginId || '').trim().toLowerCase();
  const name = String(userNm || '').trim().toLowerCase();
  return (rows || []).filter((r) => {
    if (id && !String(r.loginId || '').toLowerCase().includes(id)) return false;
    if (name && !String(r.userNm || '').toLowerCase().includes(name)) return false;
    if (status && r.status !== status) return false;
    return true;
  });
}

export function enrichUserRows(rows) {
  return (rows || []).map((r) => ({
    ...r,
    accountTypeNm: accountTypeLabel(r.accountType),
    statusNm: accountStatusLabel(r.status),
  }));
}

export function validateUsers(tenantCode, rows) {
  const active = (rows || []).filter((r) => r && r.mod !== 'D');
  const savedByUid = new Map(getCachedUsers(tenantCode).map((r) => [r.accountUid, r]));
  const ids = new Set();
  const api = isBdApiEnabled();

  for (const row of active) {
    const loginId = String(row.loginId || '').trim();
    if (!loginId) return { ok: false, message: '사용자 ID는 필수입니다.' };
    if (!/^[A-Za-z0-9._-]+$/.test(loginId)) {
      return {
        ok: false,
        message: `사용자 ID ${loginId}: 영문·숫자·점·밑줄·하이픈만 사용할 수 있습니다.`,
      };
    }
    const saved = savedByUid.get(row.accountUid);
    if (saved && String(saved.loginId || '') !== loginId) {
      return {
        ok: false,
        message: `저장된 사용자 ID는 변경할 수 없습니다. (${saved.loginId})`,
      };
    }
    const normalized = loginId.toLowerCase();
    if (ids.has(normalized)) {
      return { ok: false, message: `사용자 ID가 중복되었습니다. (${loginId})` };
    }
    ids.add(normalized);
    if (!String(row.userNm || '').trim()) {
      return { ok: false, message: `사용자 ID ${loginId}: 사용자명은 필수입니다.` };
    }
    if (row.mod === 'A' || row.mod === 'U') {
      if (!String(row.deptUid || '').trim()) {
        return { ok: false, message: `사용자 ID ${loginId}: 주관부서를 선택하세요.` };
      }
      if (api && !isDbOrgUnitId(row.deptUid)) {
        return {
          ok: false,
          message: `사용자 ID ${loginId}: 주관부서를 다시 선택하세요. (DB 부서가 아닙니다)`,
        };
      }
    }
    if (api && (row.mod === 'A' || !saved) && !String(row.password || '').trim()) {
      return { ok: false, message: `사용자 ID ${loginId}: 신규 계정은 비밀번호가 필요합니다.` };
    }
    if (!ACCOUNT_TYPE_OPTIONS.some((o) => o.value === row.accountType)) {
      return { ok: false, message: `사용자 ID ${loginId}: 계정유형을 선택하세요.` };
    }
    if (!ACCOUNT_STATUS_OPTIONS.some((o) => o.value === row.status)) {
      return { ok: false, message: `사용자 ID ${loginId}: 계정상태를 선택하세요.` };
    }
  }
  return { ok: true };
}

export function validateUserOrgMaps(rows, users, depts) {
  const active = (rows || []).filter((r) => r && r.mod !== 'D');
  const userByUid = new Map((users || []).map((r) => [r.accountUid, r]));
  const deptByUid = new Map((depts || []).map((r) => [r.deptUid, r]));
  const pairSet = new Set();

  for (const row of active) {
    const owner = userByUid.get(row.accountUid);
    if (!owner) {
      return { ok: false, message: '겸임부서에 연결된 사용자를 찾을 수 없습니다.' };
    }
    if (!row.deptUid || !deptByUid.has(row.deptUid)) {
      return { ok: false, message: '겸임부서를 선택하세요.' };
    }
    // 주관부서는 사용자 그리드에서 관리 — 겸임부서에 중복 등록 금지
    if (String(owner.deptUid || '') === String(row.deptUid)) {
      return {
        ok: false,
        message: `${owner.loginId || ''}: 주관부서는 겸임부서로 다시 등록할 수 없습니다.`,
      };
    }
    const pair = `${row.accountUid}|${row.deptUid}`;
    if (pairSet.has(pair)) {
      return { ok: false, message: '동일 사용자의 같은 겸임부서가 중복되었습니다.' };
    }
    pairSet.add(pair);
  }
  return { ok: true };
}

